import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { theme } from '../../theme/theme';

export type StatusType = 'error' | 'info' | 'light' | 'neutral' | 'strong' | 'success' | 'teal' | 'warning';

const useStatusClasses = makeStyles({
    error: {
        background: theme.colors.red[3],
        color: theme.colors.red[1],
    },
    info: {
        background: theme.colors.blue[3],
        color: theme.colors.blue[2],
    },
    gray: {
        background: theme.colors.neutral[3],
        color: theme.colors.neutral[2],
    },
    light: {
        background: theme.colors.neutral[4],
        color: theme.colors.darkBlue[5],
    },
    neutral: {
        background: theme.colors.neutral[6],
        color: theme.colors.neutral[2],
    },
    strong: {
        background: theme.colors.neutral[6],
        color: theme.colors.neutral[1],
    },
    success: {
        background: theme.colors.green[5],
        color: theme.colors.green[1],
    },
    teal: {
        background: theme.colors.green[5],
        color: theme.colors.green[4],
    },
    warning: {
        background: theme.colors.amber[4],
        color: theme.colors.amber[1],
    },
});

const useBadgeClasses = makeStyles({
    container: {
        display: 'flex',
        padding: 4,
        borderRadius: 2,
        ...theme.typography.headings.h6,
        marginBottom: 0,
        lineHeight: 'unset',
        textTransform: 'uppercase',
        '&::before': {
            content: '"\u200b"',
        },
    },
});

/**
 * Status badge component.
 * Badges accept any kind of children, but they are primarily made to be used with text.
 *
 * @param props.type status type
 * @param props.children badge content
 */
export const Badge = (props: JSX.IntrinsicElements['div'] & { type: StatusType }) => {
    const { type, ...rootProps } = props;
    const classes = useBadgeClasses();
    const statusClasses = useStatusClasses();

    return <div {...rootProps} className={clsx(classes.container, statusClasses[type], rootProps.className)} />;
};

const useStatusIconClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        ...theme.typography.headings.h6,
        color: theme.colors.neutral[1],
        marginBottom: 0,
        '& > span': {
            display: 'flex',
            alignItems: 'center',
            marginLeft: 10,
            ...theme.fonts.label,
        },
        '&.colored > span': {
            background: theme.colors.transparent,
        },
    },
    icon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
        minWidth: 24,
        minWeight: 24,
        borderRadius: '50%',
        '& > svg': {
            width: '60%',
            height: '60%',
        },
    },
});

/**
 * Status Icon component.
 * This component add a colored circle highlight around its contents, it should be primarily used with svg icons or
 * single letters.
 *
 * @param props.icon status icon, preferably an icon or a single character string
 * @param props.type status type
 * @param props.label content to be displayed beside the icon
 * @param props.colored if should apply status type css color to the `props.children`
 * @param props.children enforced type, status content to be displayed beside the icon
 */
export const StatusIcon = (
    props: JSX.IntrinsicElements['div'] & {
        type: StatusType;
        icon: React.ReactNode;
        colored?: boolean;
    },
) => {
    const { type, icon, colored = false, children, ...rootProps } = props;
    const classes = useStatusIconClasses();
    const statusClasses = useStatusClasses();
    const cls = { colored };

    return (
        <div {...rootProps} className={clsx(classes.root, cls, props.className)}>
            <div className={clsx(classes.icon, statusClasses[type])}>{icon}</div>
            {children != undefined && <span className={colored ? statusClasses[type] : ''}>{children}</span>}
        </div>
    );
};

const useStatusCardClasses = makeStyles({
    root: {
        display: 'flex',
        padding: 10,
        borderRadius: 5,
        borderLeft: '3px solid currentColor',
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        '&.large': {
            borderLeft: '5px solid currentColor',
        },
    },
});

/**
 * Card with a small highlight on the left border according with `props.type`.
 *
 * @param props.type status type
 * @param props.large uses a slightly larger border
 */
export const StatusCard = (
    props: JSX.IntrinsicElements['div'] & { type: StatusType; colored?: boolean; large?: boolean },
) => {
    const { type, large = false, ...rootProps } = props;
    const classes = useStatusCardClasses();
    const statusClasses = useStatusClasses();
    const cls = { large };

    return <div {...rootProps} className={clsx(classes.root, statusClasses[type], cls, rootProps.className)} />;
};
